import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Shield, Users, Palette, Star, ArrowRight, Quote, Award, Trophy } from 'lucide-react';
import { useRef, useEffect } from 'react';

const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_78008f49-3917-4411-8957-ba588b83fbcc/artifacts/o2yhsu3g_image%2001.jfif',
  students: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/opd0l82s_secred%20new%2001.jfif',
  playArea: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/aosrije0_65a756c4-8e2d-4ad0-b450-c78855e60bc7.jfif',
  rainbowTeacher: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/0n053mk5_secred%20new%202.jfif',
  awardCeremony: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/3n7q0iw0_secred%2003.jfif',
  awards: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/gdsm9xb4_f55c8095-49f2-4138-8cae-1ab16a988922.jfif',
  president: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/lzvj8ag3_secrade%20owneer.jfif',
};

// Color bars matching the exact image style
const COLOR_BARS = ['#8B5CF6', '#7DD3FC', '#4ADE80', '#FACC15', '#FB923C', '#EC4899'];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const Home = () => {
  const scrollRef = useRef(null);

  const highlights = [
    {
      icon: Play,
      title: 'Play-based Learning',
      description: 'Children learn best through play. Our curriculum integrates fun activities with educational goals.',
      color: 'bg-gradient-to-br from-orange-400 to-pink-500',
    },
    {
      icon: Shield,
      title: 'Safe & Secure',
      description: 'A nurturing environment with CCTV surveillance and trained staff ensuring your child\'s safety.',
      color: 'bg-gradient-to-br from-cyan-400 to-blue-500',
    },
    {
      icon: Users,
      title: 'Experienced Teachers',
      description: 'Our passionate educators are trained in early childhood development and child psychology.',
      color: 'bg-gradient-to-br from-purple-400 to-pink-500',
    },
    {
      icon: Palette,
      title: 'Fun Activities',
      description: 'Art, music, dance, and sports activities that help children discover their talents.',
      color: 'bg-gradient-to-br from-green-400 to-teal-500',
    },
  ];

  // Extended reviews for continuous scrolling
  const reviews = [
    { name: 'Priya Sharma', child: 'Mother of Arjun', review: 'Sacred Heart School has been a blessing for our family. My son loves going to school every day. The teachers are incredibly caring!', rating: 5 },
    { name: 'Rajesh Kumar', child: 'Father of Ananya', review: 'The school\'s focus on holistic development has helped my daughter grow confident and curious. Highly recommended!', rating: 5 },
    { name: 'Meena Patel', child: 'Mother of Karthik', review: 'The staff at Sacred Heart made my child\'s transition so smooth. They truly understand children and their needs.', rating: 5 },
    { name: 'Suresh Nair', child: 'Father of Diya', review: 'Best preschool in Chikmagaluru! The play-based learning approach is exactly what young children need.', rating: 5 },
    { name: 'Lakshmi Rao', child: 'Mother of Aditya', review: 'My child has developed so many skills here. The teachers are patient and the facilities are excellent.', rating: 5 },
    { name: 'Mohan Hegde', child: 'Father of Shreya', review: 'We are so happy with our decision to choose Sacred Heart. The cultural activities are amazing!', rating: 5 },
    { name: 'Kavitha Shetty', child: 'Mother of Rohan', review: 'The school provides a perfect blend of education and fun. My son has become more confident and social.', rating: 5 },
    { name: 'Prasad Gowda', child: 'Father of Nithya', review: 'Excellent teaching methodology and caring staff. One of the top preschools in the region!', rating: 5 },
    { name: 'Anitha Bhat', child: 'Mother of Vihaan', review: 'The warm and welcoming atmosphere makes children feel at home. Wonderful school experience!', rating: 5 },
    { name: 'Ramesh Iyengar', child: 'Father of Ishita', review: 'Sacred Heart has exceeded our expectations. The focus on each child\'s individual growth is remarkable.', rating: 5 },
    { name: 'Deepa Kulkarni', child: 'Mother of Aarav', review: 'From day one, my son has been excited about school. The teachers make learning so enjoyable!', rating: 5 },
    { name: 'Vinod Shenoy', child: 'Father of Tanvi', review: 'The best decision we made for our daughter\'s education. The school truly lives up to its reputation.', rating: 5 },
  ];

  // Duplicate reviews for seamless infinite scroll
  const duplicatedReviews = [...reviews, ...reviews];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section - Image First */}
      <section className="pt-16">
        {/* School Image First */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="w-full"
        >
          <img
            src={IMAGES.hero}
            alt="Sacred Heart School entrance with colorful balloon arch"
            className="w-full h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] object-cover"
            data-testid="hero-image"
          />
        </motion.div>

        {/* Color Bars - Matching the exact style from image */}
        <div className="flex justify-center items-end bg-slate-800 py-4">
          {COLOR_BARS.map((color, index) => (
            <motion.div
              key={index}
              initial={{ height: 0 }}
              animate={{ height: '80px' }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="w-12 md:w-16 lg:w-20 mx-1 rounded-t-lg"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>

        {/* Welcome Content Below */}
        <div className="bg-gradient-to-b from-slate-50 to-white py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-800 tracking-wider mb-4">
                SACRED HEART SCHOOL
              </h1>
              <p className="font-body text-lg md:text-xl text-green-600 font-semibold tracking-[0.3em] mb-6">
                LABOR OMNIA VINCIT
              </p>
              <p className="font-heading text-2xl md:text-3xl text-slate-700 mb-2">
                Where Little Minds Grow and Shine
              </p>
              <p className="font-body text-base md:text-lg text-slate-500 mb-8">
                Chikmagaluru's Premier Preschool for Nursery, LKG & UKG Programs
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/admissions"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-red-500 to-orange-500 text-white font-body font-bold text-lg rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-500/30 active:translate-y-0 active:scale-95"
                  data-testid="hero-cta-admissions"
                >
                  Admissions Open
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="https://wa.me/919481694328?text=Hello!%20I%20would%20like%20to%20inquire%20about%20admissions."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] text-white font-body font-bold text-lg rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl active:translate-y-0 active:scale-95"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp Us
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Top Preschool Badge */}
      <section className="py-8 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
            <Trophy className="w-10 h-10 text-white" />
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-white tracking-wider">
              ONE OF THE TOP PRESCHOOLS IN CHIKMAGALURU TALUK
            </h2>
            <Award className="w-10 h-10 text-white hidden md:block" />
          </motion.div>
        </div>
      </section>

      {/* President's Desk Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="font-accent text-xl text-red-500 inline-block -rotate-2 mb-2">
              Leadership
            </span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-slate-800 tracking-wider">
              FROM THE PRESIDENT'S DESK
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            {/* President Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <div className="relative">
                <div className="rounded-[2rem] overflow-hidden shadow-xl border-4 border-white">
                  <img
                    src={IMAGES.president}
                    alt="Mr. Sandy Sequeira - President"
                    className="w-full h-[350px] md:h-[400px] object-cover object-top"
                  />
                </div>
                <div className="mt-4 text-center">
                  <h3 className="font-heading text-2xl text-slate-800 tracking-wider">MR. SANDY SEQUEIRA</h3>
                  <p className="font-body text-red-500 font-semibold">President</p>
                </div>
              </div>
            </motion.div>

            {/* President's Message */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <div className="bg-white p-8 md:p-10 rounded-[2rem] shadow-lg border border-slate-100">
                <Quote className="w-12 h-12 text-red-200 mb-4" />
                <p className="font-body text-lg text-slate-700 italic leading-relaxed mb-6">
                  "A dream is not what you see in your sleep, a dream is what does not let you sleep."
                </p>
                <p className="font-body text-sm text-slate-500 mb-6">— Late A.P.J Abdul Kalam</p>
                
                <div className="space-y-4 font-body text-slate-600 leading-relaxed">
                  <p>
                    Thus we at Sacred Heart School teach our children to DREAM...
                  </p>
                  <p>
                    A school is always a 'world in miniature' where one receives a 'training for life', where effective, meaningful and joyful learning takes place. It is in school that children begin to learn lessons of life – to accept challenges, face competition, defeat and failure and rejoice at victory and triumph.
                  </p>
                  <p>
                    At Sacred Heart School we challenge and channel students to set high expectations for themselves and support them to achieve at the highest level. The challenge to be 'the best you can be' in academic achievement is supported by a holistic approach to personal learning.
                  </p>
                  <p>
                    The classroom programmes at Sacred Heart School are rich and engaging, and the range of cultural, sporting and performance opportunities provides a huge number of opportunities that allow every student access to an environment where they can participate and excel.
                  </p>
                  <p>
                    If innovation and novel ideas are a key to a bright future, then we can surely remain assured of our place under the pedagogic sun.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-cyan-50 via-white to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="font-accent text-xl text-orange-500 inline-block -rotate-2 mb-2">
                About Us
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-slate-800 tracking-wider mb-6">
                NURTURING YOUNG MINDS
              </h2>
              <p className="font-body text-lg text-slate-600 leading-relaxed mb-6">
                Sacred Heart School is a premier preschool located in Kalyan Nagar, Chikmagaluru. We believe that every child is unique and deserves an education that nurtures their individual talents and curiosity.
              </p>
              <p className="font-body text-lg text-slate-600 leading-relaxed mb-8">
                Our experienced teachers create a warm, stimulating environment where children feel safe to explore, question, and discover. Through play-based learning, we help children develop essential skills while having fun.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 font-body font-bold text-red-500 hover:text-red-600 transition-colors group"
                data-testid="about-learn-more"
              >
                Learn More About Us
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-[2.5rem] overflow-hidden shadow-xl border-4 border-white">
                <img
                  src={IMAGES.students}
                  alt="Students and teachers at Sacred Heart School"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="font-accent text-xl text-purple-500 inline-block -rotate-2 mb-2">
              Why Choose Us
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-slate-800 tracking-wider">
              WHAT MAKES US SPECIAL
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {highlights.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-[2rem] shadow-lg border border-purple-100 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl group"
                data-testid={`highlight-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-16 h-16 ${item.color} rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg`}>
                  <item.icon className="w-8 h-8 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-heading text-xl text-slate-800 tracking-wider mb-3">
                  {item.title.toUpperCase()}
                </h3>
                <p className="font-body text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="font-accent text-xl text-cyan-500 inline-block -rotate-2 mb-2">
              Our Programs
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-slate-800 tracking-wider mb-4">
              PROGRAMS DESIGNED FOR GROWTH
            </h2>
            <p className="font-body text-lg text-slate-600 max-w-2xl mx-auto">
              Age-appropriate curriculum that nurtures every aspect of your child's development
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Nursery', color: 'from-orange-400 to-yellow-400', bgColor: 'bg-gradient-to-br from-orange-50 to-yellow-50', borderColor: 'border-orange-200' },
              { name: 'LKG', color: 'from-teal-400 to-cyan-400', bgColor: 'bg-gradient-to-br from-teal-50 to-cyan-50', borderColor: 'border-teal-200' },
              { name: 'UKG', color: 'from-pink-400 to-purple-400', bgColor: 'bg-gradient-to-br from-pink-50 to-purple-50', borderColor: 'border-pink-200' },
            ].map((program, index) => (
              <motion.div
                key={program.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className={`${program.bgColor} p-8 rounded-[2rem] border-2 ${program.borderColor} transition-all duration-300 hover:-translate-y-2 hover:shadow-xl group`}
                data-testid={`program-preview-${program.name.toLowerCase()}`}
              >
                <div className={`w-full h-3 bg-gradient-to-r ${program.color} rounded-full mb-6`}></div>
                <h3 className="font-heading text-4xl text-slate-800 tracking-wider mb-4">
                  {program.name.toUpperCase()}
                </h3>
                <p className="font-body text-slate-600 mb-6">
                  Comprehensive learning program with play-based activities, creative arts, and foundational skills development.
                </p>
                <Link
                  to="/programs"
                  className="inline-flex items-center gap-2 font-body font-bold text-slate-700 hover:text-red-500 transition-colors group/link"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Continuous Running Testimonials */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="font-accent text-xl text-cyan-500 inline-block -rotate-2 mb-2">
              Testimonials
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-slate-800 tracking-wider mb-4">
              WHAT PARENTS SAY
            </h2>
            <p className="font-body text-lg text-slate-600 max-w-2xl mx-auto">
              Hear from the families who trust us with their little ones
            </p>
          </motion.div>

          {/* Continuous Scrolling Testimonials */}
          <div className="relative overflow-hidden" ref={scrollRef}>
            <div 
              className="flex gap-6 animate-scroll"
              style={{
                width: 'fit-content',
              }}
            >
              {duplicatedReviews.map((review, index) => (
                <div
                  key={index}
                  className="w-[350px] flex-shrink-0 bg-white p-8 rounded-[2rem] shadow-lg border border-blue-100"
                >
                  <Quote className="w-10 h-10 text-red-200 mb-4" />
                  <div className="flex gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400" fill="#FBBF24" />
                    ))}
                  </div>
                  <p className="font-body text-slate-600 leading-relaxed mb-6 line-clamp-4">
                    "{review.review}"
                  </p>
                  <div>
                    <p className="font-heading text-lg text-slate-800 tracking-wider">{review.name.toUpperCase()}</p>
                    <p className="font-body text-sm text-slate-500">{review.child}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="font-accent text-xl text-pink-500 inline-block -rotate-2 mb-2">
              School Life
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-slate-800 tracking-wider mb-4">
              MOMENTS OF JOY
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[IMAGES.playArea, IMAGES.rainbowTeacher, IMAGES.awardCeremony, IMAGES.awards].map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-4 border-white"
              >
                <img
                  src={img}
                  alt={`School activity ${index + 1}`}
                  className="w-full h-40 md:h-48 object-cover"
                />
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-10">
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-body font-bold rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-500/30"
            >
              View Full Gallery
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white tracking-wider mb-6">
              READY TO GIVE YOUR CHILD THE BEST START?
            </h2>
            <p className="font-body text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Join the Sacred Heart family and watch your little one blossom in our nurturing environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/admissions"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-red-500 font-body font-bold rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg active:translate-y-0 active:scale-95"
                data-testid="cta-enroll"
              >
                Enroll Today
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="https://wa.me/919481694328"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white font-body font-bold rounded-full border-2 border-white transition-all duration-300 hover:-translate-y-1 hover:bg-white/10 active:translate-y-0 active:scale-95"
                data-testid="cta-whatsapp"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
